import { ViewContainerRef } from '@angular/core';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ModalComponent } from './modal.service';

export interface Modal<T> {
	componentInstance: ModalComponent<T>;
	result: Promise<T>;
	close(result?: T);
	dismiss(reason?: any);
}

//Emily add a new interface for Dictionary
export interface IKeyedCollection<T> {
	Add(key: string, value: T);
	ContainsKey(key: string): boolean;
	Count(): number;
	Item(key: string): T;
	Keys(): string[];
	Remove(key: string): T;
	Values(): T[];
}

//initial implementation of IKeyedCollection<T>
export class KeyedCollection<T> implements IKeyedCollection<T> {
	private items: { [index: string]: T } = {};

	private count = 0;

	public ContainsKey(key: string): boolean {
		return this.items.hasOwnProperty(key);
	}

	public Count(): number {
		return this.count;
	}

	public Add(key: string, value: T) {
		if (!this.items.hasOwnProperty(key)) {
			this.count++;
		}
		this.items[key] = value;
	}

	public Remove(key: string): T {
		const val = this.items[key];
		delete this.items[key];
		this.count--;
		return val;
	}

	public Item(key: string): T {
		return this.items[key];
	}

	public Keys(): string[] {
		const keySet: string[] = [];
		for (const prop in this.items) {
			if (this.items.hasOwnProperty(prop)) {
				keySet.push(prop);
			}
		}
		return keySet;
	}

	public Values(): T[] {
		const values: T[] = [];
		for (const prop in this.items) {
			if (this.items.hasOwnProperty(prop)) {
				values.push(this.items[prop]);
			}
		}
		return values;
	}
}







// Use MutationObserver to check if we are still on the component who has trigger the modal
// and to check if the modal was close by the user (click on the close button, dismiss or
// click away from the modal)
export class ModalMO<T> implements Modal<T> {
	private observerNav: MutationObserver;
	private observerModal: MutationObserver;

	get componentInstance(): any {
		return this.modalRef.componentInstance;
	}

	get result(): Promise<T> {
		return this.modalRef.result as Promise<T>;
	}

	constructor(
		private modalRef: NgbModalRef,
		private vRef: ViewContainerRef
	) {
		// Dismiss the modal, because the user has navigating away from the component who created the modal
		this.observerNav = new MutationObserver(mutations => {
			mutations.forEach(m => {
				if (Array.from(m.removedNodes).some(n => n === vRef.element.nativeElement)) {
					this.disconnect(); // Disconnect the MutationObservers
					this.modalRef.dismiss('DOMChange'); // Dismiss the modal
					return; // Stop processing the other mutation, we don't need it
				}
			});
		});

		// Use to remove the previous MutationObserver (and himself) on a closed or dismissed modal
		this.observerModal = new MutationObserver(mutations => {
			mutations.forEach(m => {
				if (m.oldValue.split(/\s/).find(str => str === 'modal-open')
					&& isHTMLElement(m.target) && !m.target.classList.contains('modal-open')) {
					this.disconnect(); // The modal is already close, only need to disconnect the MutationObservers
					return; // Stop processing the other mutation, we don't need it
				}
			});
		});

		this.observerNav.observe(vRef.element.nativeElement.parentElement, { childList: true });
		this.observerModal.observe(document.body, { attributeOldValue: true });
	}

	close(result?: T): void {
		if (this.observerModal) {
			this.observerModal.disconnect();
		}

		if (this.observerNav) {
			this.observerNav.disconnect();
		}

		this.modalRef.close(result);
	}

	dismiss(reason?: any): void {
		if (this.observerModal) {
			this.observerModal.disconnect();
		}

		if (this.observerNav) {
			this.observerNav.disconnect();
		}

		this.modalRef.dismiss(reason);
	}

	/*
	 * Free the MutationObserver by disconnecting and setting them to null.
	 */
	private disconnect() {
		if (this.observerModal) {
			this.observerModal.disconnect();
			this.observerModal = null;
		}
		if (this.observerNav) {
			this.observerNav.disconnect();
			this.observerNav = null;
		}
	}
}


// Use the PopStateEvent to check if we are still on the component who has trigger the modal
// and use a MutationObserver to check if the modal was close by the user (click on the
// close button, dismiss or click away from the modal)
export class ModalNG<T> implements Modal<T> {
	private dismissModal_cb: EventListener;
	private observerModal: MutationObserver;

	get componentInstance(): any {
		return this.modalRef.componentInstance;
	}

	get result(): Promise<T> {
		return this.modalRef.result as Promise<T>;
	}

	constructor(private modalRef: NgbModalRef) {
		this.dismissModal_cb = this.dismissModal.bind(this);
		// User has navigating away from the original component who has trigger the modal
		window.addEventListener('popstate', this.dismissModal_cb, false);

		this.observerModal = new MutationObserver(mutations => {
			mutations.forEach(m => {
				// Modal has been close or dismiss
				if (m.oldValue.split(/\s/).find(str => str === 'modal-open')
					&& isHTMLElement(m.target) && !m.target.classList.contains('modal-open')) {
					this.disconnect(); // The modal is already close, only need to disconnect the MutationObserver
					return; // Stop processing the other mutation, we don't need it
				}
			});
		});

		// djb:bugfix-#27&28-IE11 pop ups don't render, clicking continue doesn't work (browser Syntax Error)
		// djb:ref https://developer.mozilla.org/en-US/docs/Web/API/MutationObserverInit/attributeOldValue
		this.observerModal.observe(document.body, { attributes: true, attributeOldValue: true });

	}

	close(result?: T) {
		this.modalRef.close(result);
	}
	dismiss(reason?: any) {
		this.modalRef.close(reason);
	}

	private disconnect() {
		window.removeEventListener('popstate', this.dismissModal_cb, false);
		if (this.observerModal) {
			this.observerModal.disconnect();
			this.observerModal = null;
		}
	}

	private dismissModal() {
		this.disconnect();
		this.modalRef.dismiss('HistoryChange');
	}
}

/**
 * Return true if value is an HTMLElement, otherwise return false
 */
function isHTMLElement(value: any): value is HTMLElement {
	return value.classList !== undefined;
}





